import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicJobOfferView, { LCTF_WORDMARK_SRC, OfferPortalNav, type OfferPortalNavStep } from '../components/people/hiring/PublicJobOfferView';
import type { JobOfferFields, JobOfferLetter, JobOfferPolicyAck, JobOfferSection, JobOfferSignature } from '../types/hiring';
import { getPublicSettings } from '../utils/appSettings';
import {
  assembleEmploymentAgreementSections,
  employmentAgreementIsReleased,
  employmentAgreementIsSigned,
  formatOfferDate,
  LCTF_OFFER_COMPANY,
  offerLetterIsSigned,
} from '../utils/jobOfferLetter';
import {
  ackPublicJobOfferPolicy,
  confirmPublicJobOfferStart,
  fetchPublicJobOffer,
  signPublicJobOffer,
  type PublicJobOfferPayload,
  type PublicOfferPolicy,
} from '../utils/jobOfferPublicApi';
import './PublicProposalPage.css';
import './PublicJobOfferPage.css';

type PortalStep = OfferPortalNavStep;

function parseTs(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object') {
    const raw = value as { toDate?: () => Date; _seconds?: number; seconds?: number };
    if (typeof raw.toDate === 'function') return raw.toDate();
    const seconds = typeof raw._seconds === 'number' ? raw._seconds : raw.seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
  }
  return null;
}

function parseSignature(raw: unknown): JobOfferSignature | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.dataUrl !== 'string' || !s.dataUrl) return null;
  return {
    dataUrl: s.dataUrl,
    signedBy: String(s.signedBy || ''),
    signedAt: parseTs(s.signedAt) || new Date(),
    title: typeof s.title === 'string' ? s.title : undefined,
  };
}

function offerFromPayload(payload: PublicJobOfferPayload): JobOfferLetter {
  const fields = (payload.fields || {}) as Partial<JobOfferFields>;
  const acks = Array.isArray(payload.policyAcks) ? payload.policyAcks : [];
  return {
    id: String(payload.id || ''),
    applicationId: '',
    slug: String(payload.slug || ''),
    status: payload.status === 'signed' ? 'signed' : 'sent',
    fields: {
      employeeFirstName: String(fields.employeeFirstName || ''),
      employeeLastName: String(fields.employeeLastName || ''),
      employeeEmail: String(fields.employeeEmail || ''),
      positionTitle: String(fields.positionTitle || ''),
      duties: String(fields.duties || ''),
      startDate: String(fields.startDate || ''),
      payType: fields.payType === 'hourly' ? 'hourly' : 'salary',
      payAmount: Number(fields.payAmount) || 0,
      hoursPerWeek: Number(fields.hoursPerWeek) || 40,
      employmentType: fields.employmentType || 'full-time',
      location: String(fields.location || ''),
      reportingTo: String(fields.reportingTo || ''),
      reportingToUserId: String(fields.reportingToUserId || ''),
      responseDeadline: String(fields.responseDeadline || ''),
      ceoName: String(fields.ceoName || ''),
      ceoTitle: String(fields.ceoTitle || ''),
    },
    clauses: {
      travelInstall: false,
      salaryNoOvertime: false,
      hourlyOvertime: false,
      profitSharePercent: null,
      sepIraPercent: null,
      healthReimbursementMonthly: null,
      executiveResponsibilities: false,
      executiveResponsibilitiesText: '',
      salesCommission: false,
      salesCommissionPercent: null,
      ...(payload.clauses || {}),
    } as JobOfferLetter['clauses'],
    sections: Array.isArray(payload.sections) ? payload.sections as JobOfferSection[] : [],
    agreementSections: Array.isArray(payload.agreementSections) ? payload.agreementSections as JobOfferSection[] : [],
    ceoSignature: parseSignature(payload.ceoSignature),
    employeeSignature: parseSignature(payload.employeeSignature),
    agreementCeoSignature: parseSignature(payload.agreementCeoSignature) || parseSignature(payload.ceoSignature),
    agreementEmployeeSignature: parseSignature(payload.agreementEmployeeSignature),
    policyAcks: acks.map(a => ({
      policyId: String(a.policyId || ''),
      policyTitle: String(a.policyTitle || ''),
      version: String(a.version || ''),
      acknowledgedAt: parseTs(a.acknowledgedAt) || new Date(),
    } as JobOfferPolicyAck)),
    startDateConfirmedAt: parseTs(payload.startDateConfirmedAt),
    sentAt: parseTs(payload.sentAt),
    contractSentAt: Object.prototype.hasOwnProperty.call(payload, 'contractSentAt')
      ? parseTs(payload.contractSentAt)
      : undefined,
    offerSignedAt: parseTs(payload.offerSignedAt),
    signedAt: parseTs(payload.signedAt),
    signedPdfUrl: typeof payload.signedPdfUrl === 'string' ? payload.signedPdfUrl : null,
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

const PublicJobOfferPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<JobOfferLetter | null>(null);
  const [policies, setPolicies] = useState<PublicOfferPolicy[]>([]);
  const [step, setStep] = useState<PortalStep>('offer');
  const goToStep = (next: PortalStep) => setStep(next);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openPolicyId, setOpenPolicyId] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(LCTF_WORDMARK_SRC);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    const res = await fetchPublicJobOffer(slug);
    const next = offerFromPayload(res.offer);
    setOffer(next);
    setPolicies(res.policies || []);
    if (employmentAgreementIsSigned(next)) {
      const acked = new Set(next.policyAcks.map(a => a.policyId));
      const remaining = (res.policies || []).filter(p => !acked.has(p.id));
      if (remaining.length === 0 && !next.startDateConfirmedAt) setStep('start');
      else if (remaining.length > 0) setStep(prev => (prev === 'offer' || prev === 'contract' ? 'policies' : prev));
    } else if (offerLetterIsSigned(next) && employmentAgreementIsReleased(next)) {
      setStep(prev => (prev === 'offer' ? 'contract' : prev));
    }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await getPublicSettings();
        if (cancelled) return;
        setLogoUrl(settings?.engagementLogo?.url || LCTF_WORDMARK_SRC);
        if (settings?.engagementBackground?.url) setBackgroundUrl(settings.engagementBackground.url);
      } catch {
        /* public branding is optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await load();
      } catch (e) {
        if (!cancelled) {
          const msg = (e as Error).message || 'Offer not found';
          setError(msg === 'Failed to fetch' ? 'This offer link is unavailable right now.' : msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const ackedIds = useMemo(() => new Set(offer?.policyAcks.map(a => a.policyId) || []), [offer]);
  const remainingPolicies = policies.filter(p => !ackedIds.has(p.id));
  const letterSigned = offerLetterIsSigned(offer);
  const contractReleased = employmentAgreementIsReleased(offer);
  const contractSigned = employmentAgreementIsSigned(offer);
  const policiesDone = contractSigned && remainingPolicies.length === 0;
  const startDone = Boolean(offer?.startDateConfirmedAt);

  const handleSign = async (sig: { dataUrl: string; signedBy: string }, document: 'offer' | 'agreement') => {
    if (!slug) return;
    setBusy(true);
    setActionError(null);
    try {
      await signPublicJobOffer({ slug, signedBy: sig.signedBy, dataUrl: sig.dataUrl, document });
      await load();
      setStep(document === 'offer'
        ? (employmentAgreementIsReleased(offer) ? 'contract' : 'offer')
        : (policies.length ? 'policies' : 'start'));
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleAck = async (policyId: string) => {
    if (!slug) return;
    setBusy(true);
    setActionError(null);
    try {
      await ackPublicJobOfferPolicy({ slug, policyId });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmStart = async () => {
    if (!slug) return;
    setBusy(true);
    setActionError(null);
    try {
      await confirmPublicJobOfferStart(slug);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="proposal-public-page offer-public-page">
        <div className="proposal-loading">
          <div className="loading-spinner" />
          <p>Loading your offer…</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="proposal-public-page offer-public-page">
        <div className="proposal-error">
          <h2>Offer unavailable</h2>
          <p>{error || 'This offer link is no longer active.'}</p>
        </div>
      </div>
    );
  }

  const showingDocument = step === 'offer' || step === 'contract';
  const viewingContract = step === 'contract';
  const agreementSections = offer.agreementSections.length
    ? offer.agreementSections
    : assembleEmploymentAgreementSections(offer.fields, offer.clauses);

  if (showingDocument) {
    return (
      <>
        <PublicJobOfferView
          fields={offer.fields}
          sections={viewingContract ? agreementSections : offer.sections}
          ceoSignature={viewingContract ? (offer.agreementCeoSignature || offer.ceoSignature) : offer.ceoSignature}
          employeeSignature={viewingContract ? offer.agreementEmployeeSignature : offer.employeeSignature}
          logoUrl={logoUrl || LCTF_WORDMARK_SRC}
          backgroundUrl={backgroundUrl}
          documentKind={viewingContract ? 'contract' : 'offer'}
          navStep={step}
          onNavStep={goToStep}
          offerSigned={letterSigned}
          contractSigned={contractSigned}
          contractReleased={contractReleased}
          policiesUnlocked={contractSigned}
          startUnlocked={policiesDone}
          onSign={sig => void handleSign(sig, viewingContract ? 'agreement' : 'offer')}
          signDisabled={busy}
        />
        {actionError && <p className="offer-portal-error offer-portal-error-fixed">{actionError}</p>}
      </>
    );
  }

  const viewingPolicies = step === 'policies';

  return (
    <div className="proposal-public-page offer-public-page">
      {backgroundUrl && (
        <div className="proposal-background" style={{ backgroundImage: `url(${backgroundUrl})` }} />
      )}
      <div className="proposal-background-overlay" />
      <div className="proposal-container">
        <header className="proposal-header-module">
          <div className="header-logo">
            <img src={logoUrl || LCTF_WORDMARK_SRC} alt={LCTF_OFFER_COMPANY} className="proposal-logo" />
          </div>
          <div className="header-message">
            <p className="thank-you-message">
              Welcome to <strong>{LCTF_OFFER_COMPANY}</strong>. Review the remaining onboarding steps below.
            </p>
            <p className="owner-signature">— Matt Lynch, CEO</p>
          </div>
        </header>
        <OfferPortalNav
          current={step}
          onSelect={goToStep}
          offerSigned={letterSigned}
          contractReleased={contractReleased}
          policiesUnlocked={contractSigned}
          startUnlocked={policiesDone}
        />
        <p className="offer-portal-howto">
          Complete these steps in order: sign the offer letter, sign the employment agreement, review policies, then confirm your start date.
        </p>

        {viewingPolicies ? (
          <section className="offer-portal-panel offer-paper">
            <h2>Employee handbook policies</h2>
            <p>Open and acknowledge each required policy to continue.</p>
            {policies.length === 0 && <p>No published policies need review.</p>}
            <ul className="offer-policy-list">
              {policies.map(policy => {
                const done = ackedIds.has(policy.id);
                const open = openPolicyId === policy.id;
                return (
                  <li key={policy.id} className={done ? 'is-acked' : ''}>
                    <button type="button" onClick={() => setOpenPolicyId(open ? null : policy.id)}>
                      {policy.policyNumber ? `${policy.policyNumber} — ` : ''}{policy.title}
                      {done ? ' (acknowledged)' : ''}
                    </button>
                    {open && (
                      <div className="offer-policy-body">
                        {policy.summary ? <p className="offer-policy-summary">{policy.summary}</p> : null}
                        {policy.bodyFormat === 'html' ? (
                          <div dangerouslySetInnerHTML={{ __html: policy.body }} />
                        ) : (
                          <p style={{ whiteSpace: 'pre-wrap' }}>{policy.body}</p>
                        )}
                        {!done && (
                          <button type="button" className="offer-portal-primary" disabled={busy} onClick={() => void handleAck(policy.id)}>
                            I have read and acknowledge this policy
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {policiesDone && (
              <button type="button" className="offer-portal-primary" onClick={() => goToStep('start')}>
                Continue to start date
              </button>
            )}
          </section>
        ) : (
          <section className="offer-portal-panel offer-paper">
            <h2>{startDone ? "You're hired" : 'Confirm your start date'}</h2>
            <p>
              Your start date is <strong>{formatOfferDate(offer.fields.startDate)}</strong>
              {offer.fields.positionTitle ? ` for ${offer.fields.positionTitle}` : ''}.
            </p>
            {startDone ? (
              <p>
                Welcome to {LCTF_OFFER_COMPANY}. We will be in touch with first-day details.
                Bring a government ID and be ready to complete remaining new-hire paperwork with the team.
              </p>
            ) : (
              <>
                <p>Confirm that you can begin on this date. The shop will follow up with onboarding details.</p>
                <button type="button" className="offer-portal-primary" disabled={busy} onClick={() => void handleConfirmStart()}>
                  Confirm start date
                </button>
              </>
            )}
          </section>
        )}

        {actionError && <p className="offer-portal-error">{actionError}</p>}
      </div>
    </div>
  );
};

export default PublicJobOfferPage;
